import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FileText, UploadCloud } from 'lucide-react';
import { Field, FormCard, StickyActions, Input, Select, Textarea, Button } from './ui/FormComponents.jsx';
import { getLocalities, getProvinces } from '@/services/clients.js';
import { resolveEmpresaId, resolveSucursalActivaId } from '@/utils/requestContext.js';
import { validateChoferDocumentFile } from '@/services/choferDocumentos.js';

const DOC_OPTIONS = [
  { value: 'DNI', label: 'DNI' },
  { value: 'LE', label: 'Libreta de Enrolamiento' },
  { value: 'LC', label: 'Libreta Cívica' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
];

const ESTADOS_CHOFER = [
  { value: 'ACTIVO', label: 'Activo' },
  { value: 'SUSPENDIDO', label: 'Suspendido' },
];

const LICENCIA_CLASES = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
const DOCUMENT_FIELDS = [
  { key: 'docDniFrenteUrl', label: 'DNI frente' },
  { key: 'docDniDorsoUrl', label: 'DNI dorso' },
  { key: 'docLicenciaUrl', label: 'Licencia' },
  { key: 'docAntecedentesUrl', label: 'Antecedentes' },
  { key: 'docARTUrl', label: 'ART' },
];
const PHONE_REGEX = /^(\+?\d{7,15}|0?\d{2,4}[ -]?\d{6,8})$/;
const PASSPORT_REGEX = /^[A-Za-z0-9]{6,20}$/;
const CUIL_MASK_DIGITS = 11;

function normalizeDigits(value, max) {
  return String(value || '').replace(/[^\d]/g, '').slice(0, max);
}

function formatCuilInput(value) {
  const digits = normalizeDigits(value, CUIL_MASK_DIGITS);
  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

function normalizeCuilValue(value) {
  const digits = normalizeDigits(value, CUIL_MASK_DIGITS);
  if (digits.length === 0) return '';
  return digits.length === CUIL_MASK_DIGITS ? formatCuilInput(digits) : value;
}

function validateCuilCuit(value) {
  if (!value) return true;
  const normalized = value.replace(/[^\d]/g, '');
  if (!/^(20|23|24|27|30|33|34)\d{8}\d$/.test(normalized)) {
    return false;
  }
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const digits = normalized.slice(0, 10).split('');
  const total = digits.reduce((acc, digit, index) => acc + Number(digit) * weights[index], 0);
  const mod = 11 - (total % 11);
  const expected = mod === 11 ? 0 : mod === 10 ? 9 : mod;
  return expected === Number(normalized[10]);
}

const schema = yup.object({
  apellido: yup.string().trim().required('El apellido es obligatorio').max(80, 'Máximo 80 caracteres'),
  nombre: yup.string().trim().required('El nombre es obligatorio').max(80, 'Máximo 80 caracteres'),
  apodo: yup.string().trim().max(80, 'Máximo 80 caracteres').nullable(),
  tipoDocumento: yup
    .string()
    .oneOf(DOC_OPTIONS.map(option => option.value), 'Seleccioná un tipo de documento válido')
    .required('El tipo de documento es obligatorio'),
  nroDocumento: yup
    .string()
    .required('El número de documento es obligatorio')
    .test('documento-valido', 'El número de documento no es válido', function testDocumento(value) {
      const tipo = this.parent.tipoDocumento;
      if (!value) return false;
      if (tipo === 'DNI') return /^\d{7,8}$/u.test(value);
      if (['LE', 'LC'].includes(tipo)) return /^\d{7,9}$/u.test(value);
      if (tipo === 'PASAPORTE') return PASSPORT_REGEX.test(value);
      return true;
    }),
  cuilCuit: yup
    .string()
    .nullable()
    .transform(val => (val ? val.trim() : null))
    .test('cuil-valido', 'El CUIL/CUIT no es válido', validateCuilCuit),
  fechaNacimiento: yup
    .string()
    .nullable()
    .test('fecha-nacimiento', 'La fecha de nacimiento no puede ser futura', value => {
      if (!value) return true;
      const date = new Date(value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return !Number.isNaN(date.getTime()) && date <= today;
    }),
  genero: yup.string().nullable(),
  telefonoMovil: yup
    .string()
    .required('El teléfono móvil es obligatorio')
    .test('telefono-movil', 'Formato de teléfono inválido', value => PHONE_REGEX.test(value || '')),
  telefonoAlternativo: yup
    .string()
    .nullable()
    .transform(val => (val ? val.trim() : null))
    .test('telefono-alternativo', 'Formato de teléfono inválido', value => !value || PHONE_REGEX.test(value)),
  email: yup
    .string()
    .nullable()
    .transform(val => (val ? val.trim() : null))
    .email('El correo electrónico no es válido'),
  direccionCalle: yup.string().nullable(),
  direccionNumero: yup.string().nullable(),
  direccionPiso: yup.string().nullable(),
  direccionDepto: yup.string().nullable(),
  localidad: yup.string().nullable(),
  provincia: yup.string().nullable(),
  pais: yup.string().default('AR'),
  empresaId: yup.string().required('No pudimos resolver la empresa activa'),
  sucursalId: yup.string().required('No pudimos resolver la sucursal activa'),
  legajo: yup.string().nullable(),
  fechaAlta: yup.string().required('La fecha de alta es obligatoria'),
  estado: yup.string().oneOf(ESTADOS_CHOFER.map(option => option.value)).default('ACTIVO'),
  licenciaClase: yup.array().of(yup.string().oneOf(LICENCIA_CLASES)).min(1, 'Seleccioná al menos una clase de licencia'),
  licenciaEmision: yup.string().nullable(),
  licenciaVencimiento: yup
    .string()
    .required('Indicá la fecha de vencimiento de la licencia')
    .test('vencimiento', 'La licencia debe estar vigente o el chofer debe estar suspendido', function validateLicencia(value) {
      if (!value) return false;
      const estado = this.parent.estado;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const comparable = new Date(date);
      comparable.setHours(0, 0, 0, 0);
      if (comparable >= today) return true;
      return estado === 'SUSPENDIDO';
    }),
  licenciaRestricciones: yup.string().nullable(),
  docDniFrenteUrl: yup.string().nullable(),
  docDniDorsoUrl: yup.string().nullable(),
  docLicenciaUrl: yup.string().nullable(),
  docAntecedentesUrl: yup.string().nullable(),
  docARTUrl: yup.string().nullable(),
  docOtros: yup.array().of(yup.string().nullable()),
  aptoPsicofisico: yup.boolean().default(false),
  aptoFecha: yup
    .string()
    .nullable()
    .test('apto-fecha', 'La fecha del apto es obligatoria cuando se marca apto', function validateApto(value) {
      if (!this.parent.aptoPsicofisico) return true;
      return Boolean(value);
    }),
  observaciones: yup.string().nullable().max(1000, 'Máximo 1000 caracteres'),
});

const defaultFormValues = {
  apellido: '',
  nombre: '',
  apodo: '',
  tipoDocumento: 'DNI',
  nroDocumento: '',
  cuilCuit: '',
  fechaNacimiento: '',
  genero: '',
  telefonoMovil: '',
  telefonoAlternativo: '',
  email: '',
  direccionCalle: '',
  direccionNumero: '',
  direccionPiso: '',
  direccionDepto: '',
  localidad: '',
  provincia: '',
  pais: 'AR',
  empresaId: '',
  sucursalId: '',
  legajo: '',
  fechaAlta: new Date().toISOString().slice(0, 10),
  estado: 'ACTIVO',
  licenciaClase: [],
  licenciaEmision: '',
  licenciaVencimiento: '',
  licenciaRestricciones: '',
  docDniFrenteUrl: '',
  docDniDorsoUrl: '',
  docLicenciaUrl: '',
  docAntecedentesUrl: '',
  docARTUrl: '',
  docOtros: [],
  aptoPsicofisico: false,
  aptoFecha: '',
  observaciones: '',
};

function DocumentUploader({ title, currentUrl, file, error, onChange, extra = false }) {
  return (
    <div className="rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--subpanel),0.32)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[rgb(var(--fg))]">{title}</p>
          <p className="mt-1 text-xs text-[rgb(var(--muted))]">
            PDF, JPG, PNG o WEBP hasta 6 MB.
          </p>
        </div>
        <UploadCloud className="h-4 w-4 text-[rgb(var(--muted))]" />
      </div>
      <input
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
        onChange={event => onChange(event.target.files?.[0] || null)}
        className="mt-3 block w-full text-sm text-[rgb(var(--fg))]"
      />
      {file ? <p className="mt-2 text-xs text-[rgb(var(--brand-700))]">{file.name}</p> : null}
      {!file && currentUrl ? (
        <a className="mt-2 inline-flex text-xs font-medium text-[rgb(var(--brand))] underline" href={currentUrl} target="_blank" rel="noreferrer">
          Ver documento actual
        </a>
      ) : null}
      {extra ? null : null}
      {error ? <p className="mt-2 text-xs text-red-500">{error}</p> : null}
    </div>
  );
}

export function ChoferForm({ defaultValues, onSubmit, onCancel, saving = false, showSaveAndNew = false, onSaveAndNew }) {
  const defaultValuesKey = useMemo(() => JSON.stringify(defaultValues ?? {}), [defaultValues]);
  const contextEmpresaId = String(resolveEmpresaId() || '');
  const contextSucursalId = String(resolveSucursalActivaId() || '');
  const [documentFiles, setDocumentFiles] = useState({});
  const [documentErrors, setDocumentErrors] = useState({});
  const [extraDocuments, setExtraDocuments] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [isLoadingLocalities, setIsLoadingLocalities] = useState(false);

  const mergedDefaults = useMemo(() => {
    const safeDefaults = defaultValues ?? {};
    return {
      ...defaultFormValues,
      ...safeDefaults,
      empresaId: String(safeDefaults.empresaId || contextEmpresaId || ''),
      sucursalId: String(safeDefaults.sucursalId || contextSucursalId || ''),
      cuilCuit: normalizeCuilValue(safeDefaults.cuilCuit || ''),
      docOtros: Array.isArray(safeDefaults.docOtros) ? safeDefaults.docOtros : [],
    };
  }, [contextEmpresaId, contextSucursalId, defaultValuesKey]);

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: mergedDefaults,
    mode: 'onBlur',
  });

  useEffect(() => {
    reset(mergedDefaults);
    setDocumentFiles({});
    setDocumentErrors({});
    setExtraDocuments([]);
  }, [mergedDefaults, reset]);

  useEffect(() => {
    setValue('empresaId', String(contextEmpresaId || mergedDefaults.empresaId || ''));
    setValue('sucursalId', String(contextSucursalId || mergedDefaults.sucursalId || ''));
    setValue('pais', 'AR', { shouldDirty: false, shouldValidate: false });
  }, [contextEmpresaId, contextSucursalId, mergedDefaults.empresaId, mergedDefaults.sucursalId, setValue]);

  const provinciaId = watch('provincia');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getProvinces();
        if (mounted) setProvinces(data || []);
      } catch (error) {
        console.error('Error cargando provincias', error);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!provinciaId) {
      setLocalities([]);
      return;
    }
    let mounted = true;
    (async () => {
      setIsLoadingLocalities(true);
      try {
        const data = await getLocalities(provinciaId);
        if (mounted) setLocalities(data || []);
      } catch (error) {
        console.error('Error cargando localidades', error);
      } finally {
        if (mounted) setIsLoadingLocalities(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [provinciaId]);

  const estado = watch('estado');
  const aptoPsico = watch('aptoPsicofisico');
  const licenciaVencimiento = watch('licenciaVencimiento');

  const licenciaStatus = useMemo(() => {
    if (!licenciaVencimiento) return null;
    const date = new Date(licenciaVencimiento);
    if (Number.isNaN(date.getTime())) return null;
    const today = new Date();
    const diff = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { tone: 'critical', message: 'Licencia vencida. Verificá antes de activar al chofer.' };
    if (diff <= 30) return { tone: 'warning', message: `La licencia vence en ${diff} día${diff === 1 ? '' : 's'}.` };
    return null;
  }, [licenciaVencimiento]);

  const handleDocumentChange = (key, file) => {
    if (!file) {
      setDocumentFiles(prev => ({ ...prev, [key]: null }));
      setDocumentErrors(prev => ({ ...prev, [key]: '' }));
      return;
    }
    const validationError = validateChoferDocumentFile(file);
    setDocumentErrors(prev => ({ ...prev, [key]: validationError || '' }));
    if (!validationError) {
      setDocumentFiles(prev => ({ ...prev, [key]: file }));
    }
  };

  const handleExtraDocumentsChange = files => {
    const nextDocs = Array.from(files || []).map(file => ({
      file,
      error: validateChoferDocumentFile(file),
    }));
    setExtraDocuments(nextDocs);
  };

  const buildSubmitPayload = values => ({
    ...values,
    nroDocumento:
      values.tipoDocumento === 'DNI'
        ? normalizeDigits(values.nroDocumento, 8)
        : values.nroDocumento,
    provincia:
      provinces.find(option => String(option.id) === String(values.provincia))?.nombre ||
      values.provincia,
    localidad:
      localities.find(option => String(option.id) === String(values.localidad))?.nombre ||
      values.localidad,
    empresaId: String(contextEmpresaId || values.empresaId || ''),
    sucursalId: String(contextSucursalId || values.sucursalId || ''),
    cuilCuit: values.cuilCuit ? formatCuilInput(values.cuilCuit) : '',
    pais: 'AR',
    docOtros: Array.isArray(values.docOtros) ? values.docOtros.filter(Boolean) : [],
    __documentFiles: [
      ...DOCUMENT_FIELDS
        .filter(item => documentFiles[item.key])
        .map(item => ({ key: item.key, label: item.label, file: documentFiles[item.key] })),
      ...extraDocuments
        .filter(item => item.file && !item.error)
        .map(item => ({ key: 'docOtros', label: item.file.name, file: item.file })),
    ],
  });

  const submitWithValidation = async values => {
    const hasDocumentErrors = Object.values(documentErrors).some(Boolean) || extraDocuments.some(item => item.error);
    if (hasDocumentErrors) return;
    await onSubmit?.(buildSubmitPayload(values));
  };

  const handleSaveAndNewInternal = async values => {
    if (!onSaveAndNew) return;
    const result = await onSaveAndNew(buildSubmitPayload(values));
    if (result !== false) {
      reset({
        ...defaultFormValues,
        empresaId: String(contextEmpresaId || ''),
        sucursalId: String(contextSucursalId || ''),
        fechaAlta: new Date().toISOString().slice(0, 10),
      });
      setDocumentFiles({});
      setDocumentErrors({});
      setExtraDocuments([]);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit(submitWithValidation)} noValidate>
      <input type="hidden" {...register('empresaId')} />
      <input type="hidden" {...register('sucursalId')} />

      <FormCard>
        <header className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Identificación</h2>
            <p className="text-sm text-[rgb(var(--muted))]">Datos personales y documento base del chofer.</p>
          </div>
          <span className="text-xs text-[rgb(var(--muted))]">Sucursal activa #{contextSucursalId || '—'}</span>
        </header>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <Field label="Apellido" required error={errors.apellido?.message}>
              <Input placeholder="Ej. González" {...register('apellido')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Nombre" required error={errors.nombre?.message}>
              <Input placeholder="Ej. Julieta" {...register('nombre')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Apodo" hint="Opcional" error={errors.apodo?.message}>
              <Input placeholder="Alias interno" {...register('apodo')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Tipo documento" required error={errors.tipoDocumento?.message}>
              <Select {...register('tipoDocumento')}>
                {DOC_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Número" required error={errors.nroDocumento?.message}>
              <Input
                placeholder="Sin puntos"
                inputMode={watch('tipoDocumento') === 'DNI' ? 'numeric' : undefined}
                maxLength={watch('tipoDocumento') === 'DNI' ? 8 : undefined}
                {...register('nroDocumento', {
                  onChange: event => {
                    if (watch('tipoDocumento') === 'DNI') {
                      event.target.value = normalizeDigits(event.target.value, 8);
                    }
                  },
                })}
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="CUIL / CUIT" hint="Formato XX-XXXXXXXX-X" error={errors.cuilCuit?.message}>
              <Controller
                control={control}
                name="cuilCuit"
                render={({ field }) => (
                  <Input
                    placeholder="20-12345678-3"
                    value={field.value || ''}
                    onChange={event => field.onChange(formatCuilInput(event.target.value))}
                  />
                )}
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Fecha de nacimiento" error={errors.fechaNacimiento?.message}>
              <Input type="date" {...register('fechaNacimiento')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Género" error={errors.genero?.message}>
              <Select {...register('genero')}>
                <option value="">Seleccionar</option>
                <option value="F">Femenino</option>
                <option value="M">Masculino</option>
                <option value="NB">No binario</option>
                <option value="X">Prefiero no decir</option>
              </Select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Fecha de alta" required error={errors.fechaAlta?.message}>
              <Input type="date" {...register('fechaAlta')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Estado" required error={errors.estado?.message}>
              <Select {...register('estado')}>
                {ESTADOS_CHOFER.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Legajo" hint="Opcional" error={errors.legajo?.message}>
              <Input placeholder="Legajo interno" {...register('legajo')} />
            </Field>
          </div>
        </div>
      </FormCard>

      <FormCard>
        <header className="mb-6">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Contacto</h2>
          <p className="text-sm text-[rgb(var(--muted))]">Medios de contacto y domicilio del chofer.</p>
        </header>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <Field label="Teléfono móvil" required error={errors.telefonoMovil?.message}>
              <Input placeholder="+5491122334455" {...register('telefonoMovil')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Teléfono alternativo" error={errors.telefonoAlternativo?.message}>
              <Input placeholder="Opcional" {...register('telefonoAlternativo')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Email" error={errors.email?.message}>
              <Input type="email" placeholder="nombre@empresa.com" {...register('email')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Field label="Calle" error={errors.direccionCalle?.message}>
              <Input placeholder="Calle" {...register('direccionCalle')} />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-2">
            <Field label="Número" error={errors.direccionNumero?.message}>
              <Input placeholder="123" {...register('direccionNumero')} />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-2">
            <Field label="Piso" error={errors.direccionPiso?.message}>
              <Input placeholder="5" {...register('direccionPiso')} />
            </Field>
          </div>
          <div className="col-span-6 md:col-span-2">
            <Field label="Depto" error={errors.direccionDepto?.message}>
              <Input placeholder="A" {...register('direccionDepto')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Provincia" error={errors.provincia?.message}>
              <Select
                {...register('provincia', {
                  onChange: () => {
                    setValue('localidad', '', { shouldDirty: true, shouldValidate: true });
                  },
                })}
                value={watch('provincia') || ''}
              >
                <option value="">Seleccionar provincia...</option>
                {provinces.map(option => (
                  <option key={option.id} value={String(option.id)}>
                    {option.nombre || option.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Localidad" error={errors.localidad?.message}>
              <Select {...register('localidad')} value={watch('localidad') || ''} disabled={!provinciaId}>
                <option value="">{isLoadingLocalities ? 'Cargando...' : 'Seleccionar localidad...'}</option>
                {localities.map(option => (
                  <option key={option.id} value={String(option.id)}>
                    {option.nombre || option.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="País" error={errors.pais?.message}>
              <>
                <input type="hidden" {...register('pais')} />
                <Input value="Argentina" disabled readOnly />
              </>
            </Field>
          </div>
        </div>
      </FormCard>

      <FormCard>
        <header className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Licencias</h2>
            <p className="text-sm text-[rgb(var(--muted))]">Control de habilitación y vigencia documental.</p>
          </div>
          {licenciaStatus ? (
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${licenciaStatus.tone === 'critical' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200' : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200'}`}>
              {licenciaStatus.message}
            </span>
          ) : null}
        </header>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-6">
            <Field label="Clase/s" required error={errors.licenciaClase?.message}>
              <Controller
                control={control}
                name="licenciaClase"
                render={({ field }) => (
                  <select
                    multiple
                    value={field.value ?? []}
                    onChange={event => field.onChange(Array.from(event.target.selectedOptions).map(option => option.value))}
                    className="min-h-[120px] w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  >
                    {LICENCIA_CLASES.map(clase => (
                      <option key={clase} value={clase}>
                        {`Clase ${clase}`}
                      </option>
                    ))}
                  </select>
                )}
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Fecha emisión" error={errors.licenciaEmision?.message}>
              <Input type="date" {...register('licenciaEmision')} />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-3">
            <Field label="Fecha vencimiento" required error={errors.licenciaVencimiento?.message}>
              <Input type="date" {...register('licenciaVencimiento')} />
            </Field>
          </div>
          <div className="col-span-12">
            <Field label="Restricciones médicas" error={errors.licenciaRestricciones?.message}>
              <Textarea rows={3} placeholder="Detalle restricciones o aptos especiales" {...register('licenciaRestricciones')} />
            </Field>
          </div>
        </div>
      </FormCard>

      <FormCard>
        <header className="mb-6 flex items-start gap-3">
          <div className="rounded-2xl bg-[rgba(var(--brand),0.12)] p-3 text-[rgb(var(--brand))]">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Documentación</h2>
            <p className="text-sm text-[rgb(var(--muted))]">Los archivos se guardan físicamente por tenant y quedan vinculados al chofer.</p>
          </div>
        </header>
        <div className="grid grid-cols-12 gap-4">
          {DOCUMENT_FIELDS.map(item => (
            <div key={item.key} className="col-span-12 md:col-span-6 xl:col-span-4">
              <DocumentUploader
                title={item.label}
                currentUrl={watch(item.key)}
                file={documentFiles[item.key]}
                error={documentErrors[item.key]}
                onChange={file => handleDocumentChange(item.key, file)}
              />
            </div>
          ))}
          <div className="col-span-12">
            <div className="rounded-2xl border border-[rgba(var(--border),0.7)] bg-[rgba(var(--subpanel),0.32)] p-4">
              <p className="text-sm font-semibold text-[rgb(var(--fg))]">Otros documentos</p>
              <p className="mt-1 text-xs text-[rgb(var(--muted))]">Podés adjuntar varios archivos adicionales en una sola vez.</p>
              <input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp"
                onChange={event => handleExtraDocumentsChange(event.target.files)}
                className="mt-3 block w-full text-sm text-[rgb(var(--fg))]"
              />
              {extraDocuments.length > 0 ? (
                <div className="mt-3 space-y-2">
                  {extraDocuments.map(item => (
                    <div key={`${item.file.name}-${item.file.size}`} className="text-xs">
                      <span className={item.error ? 'text-red-500' : 'text-[rgb(var(--brand-700))]'}>{item.file.name}</span>
                      {item.error ? <span className="ml-2 text-red-500">{item.error}</span> : null}
                    </div>
                  ))}
                </div>
              ) : null}
              {Array.isArray(watch('docOtros')) && watch('docOtros').length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2">
                  {watch('docOtros').map((url, index) => (
                    <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer" className="rounded-full bg-[rgba(var(--brand),0.12)] px-3 py-1 text-xs font-medium text-[rgb(var(--brand))]">
                      Documento extra {index + 1}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </FormCard>

      <FormCard>
        <header className="mb-6">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Seguridad y observaciones</h2>
          <p className="text-sm text-[rgb(var(--muted))]">Certificados médicos y notas internas.</p>
        </header>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-4">
            <Field label="Apto psicofísico" hint="Confirmá sólo si contás con el certificado vigente">
              <Controller
                control={control}
                name="aptoPsicofisico"
                render={({ field }) => (
                  <select
                    value={field.value ? 'true' : 'false'}
                    onChange={event => field.onChange(event.target.value === 'true')}
                    className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--panel))] px-3 py-2 text-sm text-[rgb(var(--fg))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ring))]"
                  >
                    <option value="false">Pendiente</option>
                    <option value="true">Apto vigente</option>
                  </select>
                )}
              />
            </Field>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Field label="Fecha apto" error={errors.aptoFecha?.message}>
              <Input type="date" disabled={!aptoPsico} {...register('aptoFecha')} />
            </Field>
          </div>
          <div className="col-span-12">
            <Field label="Observaciones" error={errors.observaciones?.message}>
              <Textarea rows={4} placeholder="Notas internas, antecedentes, seguimientos" {...register('observaciones')} />
            </Field>
          </div>
        </div>
      </FormCard>

      <StickyActions>
        <div className="flex items-center gap-3">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          {showSaveAndNew ? (
            <Button type="button" variant="secondary" onClick={handleSubmit(handleSaveAndNewInternal)} disabled={saving}>
              Guardar y cargar otro
            </Button>
          ) : null}
          <Button type="submit" disabled={saving || !isDirty || Object.values(documentErrors).some(Boolean) || extraDocuments.some(item => item.error)}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </StickyActions>
    </form>
  );
}

export default ChoferForm;
